<template>
  <div style="position: relative;">
    <b-card
      v-if="Boolean(comment.masked) && (isTeacher || isCommentOwner(comment))"
      class="w-100 h-100 card-overlay text-center"
    >
      <b-card-text>
        <b-icon icon="eye-slash" font-scale="3"></b-icon>
      </b-card-text>
    </b-card>
    <b-card class="message">
      <template v-slot:header>
        <b-iconstack
          v-if="comment.answered_by_count > 0"
          class="msg-type-icon"
          font-scale="1.5"
        >
          <b-icon stacked icon="chat-fill" variant="primary"></b-icon>
          <b-icon
            stacked
            icon="chat"
            variant="primary"
            shift-h="3"
            shift-v="-1"
            flip-h
          ></b-icon>
        </b-iconstack>
        <b-btn
          v-if="
            Boolean(comment.flagged) && (isCommentOwner(comment) || isTeacher)
          "
          @click="openPropPanel('modify')"
          variant="link"
          class="msg-warn-btn opacity-1"
          :disabled="isTeacher || dlibr.status == 0"
        >
          <b-icon icon="exclamation-circle-fill" variant="warning"></b-icon>
        </b-btn>

        <small class="font-weight-bolder text-primary d-block">{{
          comment.user.nickname
        }}</small>
        <small class="text-muted"
          >{{ comment.created_at | formatDateTime }}
        </small>
      </template>
      <b-card-text
        v-if="Boolean(comment.masked) && !isCommentOwner(comment) && !isTeacher"
      >
        <div class="text-center">
          <b-icon icon="eye-slash" font-scale="3" animation="fade"></b-icon>
          <p>Publication masquée pour tous.</p>
        </div>
      </b-card-text>
      <b-card-text v-else>
        <span :class="`text-${typeMap[comment.type].variant}`">{{
          typeMap[comment.type].text
        }}</span>
        <p>
          {{ comment.comment }}
        </p>
        <template
          v-if="(flowDlibr.evalMode && isTeacher) || isCommentOwner(comment)"
        >
          <div class="text-right" v-if="comment.comment_criteria">
            <b-badge pill>
              {{ comment.comment_criteria.comment }}
            </b-badge>
          </div>
          <div class="text-right" v-if="comment.reference_criteria">
            <b-badge pill>
              {{ comment.reference_criteria.comment }}
            </b-badge>
          </div>
        </template>
      </b-card-text>
      <template v-slot:footer>
        <div class="d-flex justify-content-between">
          <div>
            <b-btn
              v-if="
                isStudent &&
                  !Boolean(comment.masked) &&
                  !routeIsAnswer &&
                  comment.answered_by_count == 0
              "
              size="sm"
              variant="none"
              v-b-tooltip.hover.top="'Écrire un commentaire'"
              @click="moveToAnswers(comment.id)"
            >
              <b-icon flip-h icon="reply-fill" variant="secondary" to></b-icon>
            </b-btn>
            <b-btn
              v-if="
                comment.answered_by_count > 0 &&
                  (!Boolean(comment.masked) || isTeacher)
              "
              @click="moveToAnswers(comment.id)"
              size="sm"
              variant="none"
              v-b-tooltip.hover.top="'Commentaires(s)'"
            >
              <b-icon icon="chat-fill" variant="secondary"></b-icon>
              <span class="ml-2"
                ><small>{{ comment.answered_by_count }}</small></span
              >
            </b-btn>
            <b-btn
              v-if="!Boolean(comment.masked) && comment.url"
              :href="comment.url"
              v-b-tooltip.hover
              :title="comment.url"
              size="sm"
              variant="none"
              target="_blank"
            >
              <b-icon icon="link45deg" variant="primary"></b-icon>
            </b-btn>
          </div>
          <div>
            <b-btn
              v-if="isStudent && isCommentOwner(comment)"
              :disabled="dlibr.status == 0"
              @click="openPropPanel('modify')"
              size="sm"
              variant="none"
              v-b-tooltip.hover.top="'Modifier votre publication'"
            >
              <b-icon icon="pencil" variant="primary"></b-icon>
            </b-btn>
            <b-btn
              v-if="isStudent && !isCommentOwner(comment)"
              @click="openPropPanel('flag')"
              size="sm"
              variant="none"
              :disabled="Boolean(comment.flagged) || dlibr.status == 0"
              v-b-tooltip.hover.top="'Signaler cette intervention'"
            >
              <b-icon
                icon="flag-fill"
                :variant="comment.flagged ? 'warning' : 'primary'"
              ></b-icon>
            </b-btn>

            <template v-if="isTeacher && flowDlibr.evalMode">
              <b-btn
                v-b-tooltip.hover
                :title="
                  Boolean(comment.masked)
                    ? 'Démasquer la publication'
                    : 'Masquer la publication'
                "
                size="sm"
                variant="none"
                @click="show = !show"
              >
                <b-spinner
                  class="text-secondary"
                  small
                  v-if="maskedLoading"
                ></b-spinner>
                <b-icon
                  v-else
                  @click="toggleMaskComment(comment)"
                  icon="eye-slash"
                  :variant="Boolean(comment.masked) ? 'danger' : 'primary'"
                ></b-icon>
                <!-- add tool tip -->
              </b-btn>
              <b-btn
                v-b-tooltip.hover
                title="Évaluer la publication"
                @click="openPropPanel('grade')"
                size="sm"
                variant="none"
              >
                <div class="text-primary rotate">A+</div>
              </b-btn>
              <b-btn
                v-b-tooltip.hover
                title="Écrire un message"
                @click="openPropPanel('message')"
                size="sm"
                variant="none"
              >
                <b-icon icon="envelope" variant="primary"></b-icon>
              </b-btn>
            </template>
          </div>
        </div>
      </template>
    </b-card>
  </div>
</template>

<script>
/**
 * @file       Comment component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import { commentsMixin } from "@/modules/comments/mixins";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "commentUnit",
  mixins: [commentsMixin],
  props: {
    comment: {
      type: Object,
      default: () => {},
      required: true
    }
  },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      maskedLoading: false
    };
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Open prop panel.
     * 
     * @param   {Object}   mode       The mode
     */
    openPropPanel(mode) {
      this.$store.commit("deliberations/setPubToEdit", {
        pub: this.comment,
        mode
      });
      this.$store.commit("comments/setMsgToEdit", {});
      this.$root.$emit("bv::toggle::collapse", "property-panel");
    }
  }
};
</script>

<style lang="scss" scoped>
.card {
  margin-bottom: 1rem;
  margin-top: 1rem;
  border-radius: 15px 15px 15px 15px !important;
}
.message {
  .card-header {
    border-bottom: none;
    border-radius: 15px 15px 0 0 !important;
    .msg-type-icon {
      z-index: 1;
      position: absolute;
      top: -16px;
    }
    .msg-warn-btn {
      z-index: 1;
      position: absolute;
      top: 16px;
      right: -12px;
    }
  }
  .card-footer {
    z-index: 1;
    border-top: none;
    border-radius: 0 0 15px 15px !important;
    min-height: 3.8rem !important;
    .py-footer {
      padding: 1rem 0 1rem 0;
    }
  }
}

.card-overlay {
  background-color: white;
  position: absolute;
  z-index: 1;
  opacity: 0.6;
  margin-top: 0;
  svg {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #ececec;
  }
}

.rotate {
  transform: rotate(20deg);
  -webkit-transform: rotate(-20deg);
  -moz-transform: rotate(-20deg);
  font-weight: 590 !important;
  font-size: 1rem;
  padding: 0 0 0.2rem !important;
  opacity: 0.92;
}

.opacity-1 {
  opacity: 1 !important;
}
</style>
